// One saved label belongs to one jar. Shopify owns the parent/child removal.
(() => {
  const root = (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  let saving = false;
  let adding = false;
  let checking = false;
  let checkoutReplay = null;
  let merging = false;
  let mergeScheduled = false;
  let mergeNeedsReload = false;
  const drafts = new Map();
  const rules = window.AGFNHalloweenNoteRules;
  if (!rules) return;
  const clean = rules.clean;
  const words = value => value ? value.split(/\s+/).length : 0;
  const editors = () => [...document.querySelectorAll('[data-agfn-note-editor]')];
  const related = ref => editors().filter(node => node.dataset.jarRef === ref);
  const readCart = async () => {
    const response = await fetch(root + 'cart.js', {credentials:'same-origin', cache:'no-store'});
    if (!response.ok) throw Error('CART_READ');
    return response.json();
  };
  const noteLine = (cart, ref, id) => cart.items.find(item => String(item.variant_id) === id && item.properties?.['_AGFN jar ref'] === ref);
  const validLink = (cart, line) => {
    const parent = cart.items.find(item => item.key === line?.parent_relationship?.parent_key);
    return parent && parent.properties?.['_AGFN jar ref'] === line.properties?.['_AGFN jar ref'] && parent.quantity === 1 && line.quantity === 1;
  };
  // A jar needs its unique reference only while a note is attached. Shopify
  // cannot clear all properties in place; move orphaned quantities into a
  // matching ordinary line and remove the old lines in one update request.
  function mergeContext(cart) {
    const parents = new Set(cart.items.map(item => item.parent_relationship?.parent_key).filter(Boolean));
    const standalone = item => !parents.has(item.key) && !item.parent_relationship &&
      !item.properties?.['Halloween note'] && !item.has_components && !item.item_components?.length;
    const orphan = item => standalone(item) && Boolean(item.properties?.['_AGFN jar ref']);
    const kind = (item, dropRef = false) => JSON.stringify([
      String(item.variant_id), item.selling_plan_allocation?.selling_plan?.id || null, item.original_price,
      Object.entries(item.properties || {}).filter(([key]) => !dropRef || key !== '_AGFN jar ref').sort(([a],[b]) => a.localeCompare(b))
    ]);
    return {standalone, orphan, kind};
  }
  function mergePlan(cart) {
    const {standalone, orphan, kind} = mergeContext(cart);
    const groups = new Map();
    for (const item of cart.items) {
      if (!standalone(item)) continue;
      const key = kind(item, orphan(item));
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    }
    const updates = {}, removedRefs = [];
    for (const group of groups.values()) {
      const orphans = group.filter(orphan);
      if (!orphans.length) continue;
      const target = group.find(item => !orphan(item)) || orphans[0];
      const sources = orphans.filter(item => item !== target);
      if (!sources.length) continue;
      updates[target.key] = target.quantity + sources.reduce((sum,item) => sum + item.quantity, 0);
      for (const item of sources) {
        updates[item.key] = 0;
        removedRefs.push(item.properties['_AGFN jar ref']);
      }
    }
    return {updates, removedRefs};
  }
  function cartContents(cart) {
    const {orphan,kind} = mergeContext(cart), counts = new Map();
    for (const item of cart.items) {
      const parent = cart.items.find(candidate => candidate.key === item.parent_relationship?.parent_key);
      const key = JSON.stringify([kind(item,orphan(item)), parent ? kind(parent) : null]);
      counts.set(key, (counts.get(key) || 0) + item.quantity);
    }
    return JSON.stringify([...counts].sort(([a],[b]) => a.localeCompare(b)));
  }
  function mergeStatus(text, error = false) {
    const host = document.querySelector('#main-cart-items') || document.querySelector('#halo-cart-sidebar .previewCart-wrapper');
    if (!host) return;
    let status = host.querySelector('[data-agfn-merge-status]');
    if (!status) {status = document.createElement('p');status.dataset.agfnMergeStatus='';host.prepend(status);}
    status.className = 'agfn-cart-note__status' + (error ? ' is-error' : '');
    status.setAttribute('role',error ? 'alert' : 'status');status.textContent=text;
    if (error) {const link=document.createElement('a');link.href=root+'cart';link.textContent=' Reload cart';status.append(link);}
  }
  async function mergePlainJars() {
    if (adding || merging || saving || checking || mergeNeedsReload) return;
    merging = true;
    const controls = new Map();
    document.querySelectorAll('[data-cart-quantity], [data-cart-remove], .btn-quantity, [name="checkout"], #cart-checkout, #cart-sidebar-checkout, [data-agfn-note-save]').forEach(node => {
      if ('disabled' in node) {controls.set(node,node.disabled);node.disabled=true;}
    });
    let changed = false;
    try {
      const before = await readCart(), plan = mergePlan(before);
      if (!plan.removedRefs.length) return;
      mergeStatus('Updating your cart…');
      let response;
      try {response=await fetch(root+'cart/update.js',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates:plan.updates})});}
      catch { /* Reconcile a possibly accepted request; never replay quantity moves. */ }
      const after = await readCart();
      if (cartContents(before) !== cartContents(after) || after.items.some(item => plan.removedRefs.includes(item.properties?.['_AGFN jar ref']))) throw Error(response && !response.ok ? 'MERGE_REJECTED' : 'MERGE_UNCERTAIN');
      changed = true;
      await refresh(after);
      mergeStatus('Cart updated.');
    } catch {
      mergeNeedsReload = true;
      mergeStatus(changed ? 'Your jars were combined. Reload to see the updated cart.' : 'We could not confirm the cart update. Reload to check your quantities before continuing.',true);
    } finally {
      controls.forEach((disabled,node) => {node.disabled=disabled;});
      merging=false;
    }
  }
  function scheduleMerge(cart) {
    if (adding || mergeScheduled || merging || mergeNeedsReload || saving || checking) return;
    if (cart?.items && !mergePlan(cart).removedRefs.length) return;
    mergeScheduled=true;
    setTimeout(() => {mergeScheduled=false;mergePlainJars();},0);
  }
  document.addEventListener('cart-update',event => scheduleMerge(event.detail));
  if (document.querySelector('#main-cart-items')) scheduleMerge();
  function message(editor, text, error = false) {
    const status = editor.querySelector('[data-agfn-note-status]');
    status.textContent = text;
    status.classList.toggle('is-error', error);
  }
  function count(editor) {
    const input = editor.querySelector('[data-agfn-note-input]');
    editor.querySelector('[data-agfn-note-count]').textContent = `${words(clean(input.value))} / 12 words · ${input.value.length} / 80 characters`;
    const problem = input.value ? rules.error(input.value) : '';
    input.setAttribute('aria-invalid',String(Boolean(problem)));
    message(editor,problem,Boolean(problem));
  }
  function setPanel(editor, open) {
    const ref = editor.dataset.jarRef;
    if (open && !drafts.has(ref)) drafts.set(ref, {value:editor.querySelector('[data-agfn-note-input]').value, originalNote:editor.querySelector('[data-agfn-saved-note]').textContent});
    if (!open) drafts.delete(ref);
    editor.querySelector('[data-agfn-note-panel]').hidden = !open;
    editor.querySelector('[data-agfn-note-edit]').setAttribute('aria-expanded', String(open));
    if (open) { count(editor); editor.querySelector('[data-agfn-note-input]').focus(); }
    else editor.querySelector('[data-agfn-note-edit]').focus();
  }
  function busy(editor, state) {
    editor.setAttribute('aria-busy', String(state));
    editor.querySelectorAll('button,textarea').forEach(control => { control.disabled = state; });
    editor.querySelector('[data-agfn-note-save]').textContent = state ? 'Saving…' : 'Save note';
  }
  async function refresh(cart, ref) {
    const requests = [];
    let focusRef = ref;
    const rememberFocus = () => {
      const activeRef = document.activeElement?.closest('[data-agfn-note-editor]')?.dataset.jarRef;
      if (activeRef && drafts.has(activeRef)) focusRef = activeRef;
    };
    const section = document.querySelector('#main-cart-items')?.dataset.id;
    if (section) requests.push(fetch(root + 'cart?section_id=' + encodeURIComponent(section), {cache:'no-store'}).then(async response => {
      if (!response.ok) throw Error('RENDER');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const list = page.querySelector('.cart-list');
      if (!list) throw Error('RENDER');
      rememberFocus();
      document.querySelector('#main-cart-items .cart-list').replaceChildren(...list.childNodes);
      for (const selector of ['.cart-total-subtotal','.cart-total-grandtotal','.cart-total-savings']) {
        const current = document.querySelector('[data-cart-total] ' + selector);
        const fresh = page.querySelector('[data-cart-total] ' + selector);
        if (current && fresh) current.replaceWith(fresh);
      }
    }));
    const drawer = document.querySelector('#halo-cart-sidebar .previewCart-wrapper');
    if (drawer) requests.push(fetch(root + 'cart?view=ajax_side_cart', {cache:'no-store'}).then(async response => {
      if (!response.ok) throw Error('RENDER');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!page.querySelector('.previewCart')) throw Error('RENDER');
      rememberFocus();
      drawer.replaceChildren(...page.body.childNodes);
    }));
    await Promise.all(requests);
    const presentRefs = new Set(cart.items.map(item => item.properties?.['_AGFN jar ref']));
    for (const key of drafts.keys()) if (!presentRefs.has(key)) drafts.delete(key);
    // Section HTML carries saved notes; restore other open drafts after replacement.
    editors().forEach(editor => {
      const draft = drafts.get(editor.dataset.jarRef);
      if (!draft) return;
      editor.querySelector('[data-agfn-note-input]').value = draft.value;
      editor.querySelector('[data-agfn-note-panel]').hidden = false;
      editor.querySelector('[data-agfn-note-edit]').setAttribute('aria-expanded','true');
      count(editor);
    });
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = window.AGFNCartCount(cart); });
    document.dispatchEvent(new CustomEvent('cart-update', {detail:cart}));
    const current = related(ref);
    current.forEach(editor => message(editor, 'Saved.'));
    const visible = related(focusRef).find(editor => editor.getClientRects().length);
    visible?.querySelector(drafts.has(focusRef) ? '[data-agfn-note-input]' : '[data-agfn-note-edit]').focus();
  }
  // Reuse the saved-note renderer so personalized adds follow Ella's sidebar
  // flow, including current line keys, drafts, counts and shipping messaging.
  window.AGFNHalloweenCart = {
    beginAdd() {
      if (adding || merging || saving || checking || mergeNeedsReload) throw Error('CART_BUSY');
      adding = true;
      return () => { adding = false; };
    },
    async open(cart) {
      const drawer = document.querySelector('#halo-cart-sidebar');
      if (!drawer?.querySelector('.previewCart-wrapper')) throw Error('DRAWER_UNAVAILABLE');
      await refresh(cart);
      document.querySelectorAll('[data-free-shipping-wrapper]').forEach(node => {
        node.dispatchEvent(new Event('change', {bubbles:true}));
      });
      document.body.classList.add('cart-sidebar-show');
      // Match Ella's 500ms visibility transition and resolve the current node.
      setTimeout(() => {
        const currentDrawer = document.querySelector('#halo-cart-sidebar');
        if (currentDrawer && document.body.classList.contains('cart-sidebar-show') && !document.querySelector('dialog[open]') && !currentDrawer.contains(document.activeElement)) {
          currentDrawer.querySelector('[data-close-cart-sidebar]')?.focus({preventScroll:true});
        }
      }, 550);
    }
  };
  async function save(editor) {
    if (saving) return;
    const input = editor.querySelector('[data-agfn-note-input]');
    const value = clean(input.value);
    const problem = rules.error(value);
    if (problem) {
      input.setAttribute('aria-invalid','true');
      message(editor, problem, true);
      input.focus();
      return;
    }
    saving = true;
    const ref = editor.dataset.jarRef, id = editor.dataset.variantId;
    related(ref).forEach(node => busy(node,true));
    message(editor,'Saving your note…');
    let verified = null;
    try {
      const before = await readCart();
      if (before.currency !== 'USD') throw Error('USD_REQUIRED');
      const line = noteLine(before,ref,id);
      if (!validLink(before,line)) throw Error('LINE_CHANGED');
      const originalNote = drafts.get(ref)?.originalNote ?? editor.querySelector('[data-agfn-saved-note]').textContent;
      if (line.properties['Halloween note'] !== originalNote) throw Error('NOTE_CHANGED');
      const properties = {...line.properties, 'Halloween note':value};
      // change.js replaces the entire properties object, so retain every other field.
      let response;
      try { response = await fetch(root+'cart/change.js', {method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:line.key,quantity:1,properties})}); }
      catch { /* A readback below reconciles a possibly accepted write. */ }
      const after = await readCart();
      const saved = noteLine(after,ref,id);
      if (!validLink(after,saved) || saved.properties['Halloween note'] !== value || Object.entries(properties).some(([key,item]) => saved.properties[key] !== item)) throw Error(response && !response.ok ? 'SAVE_REJECTED' : 'SAVE_UNCERTAIN');
      verified = after;
      drafts.delete(ref);
      related(ref).forEach(node => { node.querySelector('[data-agfn-saved-note]').textContent=value; node.querySelector('[data-agfn-note-input]').value=value; });
      await refresh(after,ref);
    } catch (error) {
      if (verified) {
        // The save succeeded; a page refresh restores changed keys/removal handlers.
        related(ref).forEach(node => {
          message(node,'Your note was saved. Reload the cart to see the latest version.',true);
          node.querySelector('[data-agfn-note-panel]').hidden=true;
          node.dataset.needsReload='true';
        });
      } else {
        const text = error.message === 'NOTE_CHANGED' ? 'This note changed in another tab. Reload the cart, then edit its latest version.' : error.message === 'LINE_CHANGED' ? 'This jar or its label was removed. Reload your cart to review it.' : 'We could not confirm this save. Your text is still here. Reload the cart before trying again.';
        message(editor,text,true);
      }
    } finally {
      saving = false;
      related(ref).forEach(node => busy(node,false));
    }
  }
  function alertCheckout(control,text) {
    let alert = control.parentElement.querySelector('[data-agfn-checkout-alert]');
    if (!alert) { alert=document.createElement('p'); alert.className='agfn-cart-note__status is-error'; alert.dataset.agfnCheckoutAlert=''; alert.setAttribute('role','alert'); alert.tabIndex=-1; control.before(alert); }
    alert.textContent=text;
    alert.focus();
  }
  document.addEventListener('input', event => {
    if (event.target.matches('[data-agfn-note-input]')) {
      const editor = event.target.closest('[data-agfn-note-editor]');
      const draft = drafts.get(editor.dataset.jarRef);
      if (draft) draft.value = event.target.value;
      count(editor);
    }
  });
  document.addEventListener('click', async event => {
    const target = event.target.closest?.('button,a,input');
    if (!target) return;
    if ((adding || merging || mergeNeedsReload) && target.matches('[data-cart-remove], .btn-quantity, [data-open-edit-cart], [data-agfn-note-edit], [data-agfn-note-save], #cart-checkout, #cart-sidebar-checkout, [name="checkout"]')) {
      event.preventDefault();event.stopImmediatePropagation();return;
    }
    if (target.matches('[data-agfn-linked-line] .btn-quantity, [data-agfn-linked-line] [data-open-edit-cart]')) {event.preventDefault();event.stopImmediatePropagation();return;}
    const editor = target.closest('[data-agfn-note-editor]');
    if (editor && target.matches('[data-agfn-note-edit],[data-agfn-note-cancel],[data-agfn-note-save]')) {
      event.preventDefault();event.stopImmediatePropagation();
      if (target.matches('[data-agfn-note-edit]')) {message(editor,'');setPanel(editor,true);}
      if (target.matches('[data-agfn-note-cancel]')) {editor.querySelector('[data-agfn-note-input]').value=editor.querySelector('[data-agfn-saved-note]').textContent;message(editor,'');setPanel(editor,false);}
      if (target.matches('[data-agfn-note-save]')) await save(editor);
      return;
    }
    if (!target.matches('#cart-checkout,#cart-sidebar-checkout,[name="checkout"]')) return;
    if (checkoutReplay === target) {checkoutReplay=null;return;}
    const feeRows = [...document.querySelectorAll('[data-agfn-fee-line]')];
    if (!feeRows.length) return;
    event.preventDefault();event.stopImmediatePropagation();
    if (saving || checking) {alertCheckout(target,'Please wait while your note is saved.');return;}
    if (editors().some(node => node.dataset.needsReload)) {
      alertCheckout(target,'Your note was saved. Reload the cart to see the latest version before checking out.');
      return;
    }
    const unfinished=editors().find(node => !node.querySelector('[data-agfn-note-panel]').hidden);
    if (unfinished) {
      const text='Save or cancel your note before checking out.';
      alertCheckout(target,text);
      const visible = related(unfinished.dataset.jarRef).find(node => {
        const rect=node.getBoundingClientRect();
        return rect.width>0 && rect.height>0 && rect.right>0 && rect.left<window.innerWidth && getComputedStyle(node).visibility!=='hidden';
      });
      if (visible) {setPanel(visible,true);message(visible,text,true);}
      return;
    }
    checking=true;
    try {
      const cart=await readCart();
      const ids=new Set(feeRows.map(row => row.dataset.agfnFeeLine));
      const fees=cart.items.filter(item => ids.has(String(item.variant_id)));
      if (cart.currency !== 'USD') {alertCheckout(target,'Personalized jars are available in USD only.');return;}
      const invalid=fees.some(line => !validLink(cart,line) || rules.error(line.properties?.['Halloween note'] || '') || line.original_price!==1000 || Boolean(line.selling_plan_allocation));
      if (invalid) {alertCheckout(target,'A spooky note needs its own jar. Remove the label item, then add it from your jar’s product page.');return;}
      checkoutReplay=target;
      target.click();
    } catch {alertCheckout(target,'We could not check your notes. Please reload the cart and try again.');}
    finally {checking=false;}
  },true);
})();
